import { Info } from 'lucide-react';
import { useState } from 'react';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Textarea } from '~/components/ui/textarea';
import { useAuthStore } from '~/store/auth.store';

import {
  useBatchNeedsAttention,
  useBatchRuns,
  useStartBatchRetry,
} from '../../hooks/use-batch-retry';
import type { BatchRetryRequest, BatchRetryScope } from '../../types/batch-retry.api.types';

import { ReasonBars } from './batch-retry-shared';
import { reasonsToBars, summarizeNeedsAttention } from './batch-retry-utils';

export interface BatchRetryDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected scope radio (batch mode). */
  initialScope?: BatchRetryScope;
  /** Set = "Retry what retry #n left" mode (no scope radios; POST with fromRunId). */
  fromRunId?: string;
}

/**
 * Retry dialog: pick what to retry (batch mode) or confirm re-running one retry's leftovers.
 * The body mounts per open, so scope, reason and the idempotency key start fresh each time.
 */
export function BatchRetryDialog(props: BatchRetryDialogProps) {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <RetryDialogBody {...props} />}
    </Dialog>
  );
}

function RetryDialogBody({
  jobId,
  onOpenChange,
  initialScope = 'REMAINING',
  fromRunId,
}: BatchRetryDialogProps) {
  const [scope, setScope] = useState<BatchRetryScope>(initialScope);
  const [reason, setReason] = useState('');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const requestedBy = useAuthStore((s) => s.user?.email);
  const { data: na, isLoading: naLoading } = useBatchNeedsAttention(jobId);
  const { data: runs } = useBatchRuns(jobId);
  const { mutate: startRetry, isPending } = useStartBatchRetry(jobId);

  const s = summarizeNeedsAttention(na);
  const fromRun = fromRunId ? runs?.runs.find((r) => r.runId === fromRunId) : undefined;
  const options: { scope: BatchRetryScope; label: string; sub: string; count: number }[] = [
    {
      scope: 'REMAINING',
      label: 'All remaining',
      sub: 'Failed products that can be retried, plus products never reached',
      count: s.failedRetryable + s.notAttempted,
    },
    {
      scope: 'FAILED',
      label: 'Failed only',
      sub: 'Products that were tried and failed',
      count: s.failedRetryable,
    },
    {
      scope: 'NOT_ATTEMPTED',
      label: 'Not attempted only',
      sub: 'Products the batch never reached',
      count: s.notAttempted,
    },
  ];
  const visible = options.filter((o) => o.count > 0);
  // The pre-selected scope may have nothing in it (its radio is hidden): use the first shown.
  const effectiveScope: BatchRetryScope = visible.some((o) => o.scope === scope)
    ? scope
    : (visible[0]?.scope ?? scope);
  const selected = options.find((o) => o.scope === effectiveScope);
  const fromRunCount = (fromRun?.failed ?? 0) + (fromRun?.notAttempted ?? 0);
  const retryNumber = runs ? Math.min(runs.attemptsUsed + 1, runs.maxAttempts) : null;
  const bars =
    effectiveScope === 'NOT_ATTEMPTED' || fromRunId ? [] : reasonsToBars(s.failedReasons);

  function handleStart() {
    const body: BatchRetryRequest = {
      ...(fromRunId ? { fromRunId } : { scope: effectiveScope }),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      idempotencyKey,
      ...(requestedBy ? { requestedBy } : {}),
    };
    startRetry(body, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  }

  return (
    <>
      <DialogContent
        onInteractOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
        data-testid="batch-retry-dialog"
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle data-testid="batch-retry-dialog-title" className="text-lg font-semibold">
            {fromRunId
              ? `Retry what retry #${String(fromRun?.attempt ?? '?')} left?`
              : 'Retry what is left?'}
          </DialogTitle>
          <DialogDescription>
            {fromRunId
              ? `Re-runs the ${fromRunCount.toLocaleString()} products that retry #${String(fromRun?.attempt ?? '')} did not enrich.`
              : 'Pick which products to run again.'}
          </DialogDescription>
        </DialogHeader>

        {!fromRunId && (
          <RadioGroup
            value={effectiveScope}
            onValueChange={(v) => {
              setScope(v as BatchRetryScope);
            }}
            className="gap-2"
            data-testid="batch-retry-dialog-scope"
          >
            {visible.map((o) => (
              <label
                key={o.scope}
                htmlFor={`retry-scope-${o.scope}`}
                className="hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5"
              >
                <RadioGroupItem
                  id={`retry-scope-${o.scope}`}
                  data-testid={`batch-retry-scope-${o.scope.toLowerCase()}`}
                  value={o.scope}
                  className="mt-0.5 size-4"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {o.label}{' '}
                    <span className="text-muted-foreground tabular-nums">
                      {o.count.toLocaleString()}
                    </span>
                  </p>
                  <p className="text-muted-foreground text-xs">{o.sub}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
        )}

        {bars.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Why these failed</p>
            <ReasonBars reasons={bars} testId="batch-retry-dialog-reasons" />
          </div>
        )}

        <div
          data-testid="batch-retry-dialog-note"
          className="bg-info-50 text-foreground flex gap-2 rounded-lg px-3 py-2.5 text-xs"
        >
          <Info className="text-info mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <ul className="space-y-1">
            {s.failedPermanent > 0 && (
              <li>
                {s.failedPermanent.toLocaleString()} permanently failed product
                {s.failedPermanent === 1 ? ' is' : 's are'} skipped; fix the source data to enrich
                them.
              </li>
            )}
            <li>Retrying runs enrichment again, which uses credits; approval covers the cost.</li>
            {retryNumber !== null && runs && (
              <li>
                Retry {retryNumber} of {runs.maxAttempts} · retried products join this batch&apos;s
                approval.
              </li>
            )}
          </ul>
        </div>

        <Textarea
          data-testid="batch-retry-dialog-reason"
          aria-label="Reason (optional)"
          placeholder="Reason (optional)"
          maxLength={500}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          className="min-h-16"
        />

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            data-testid="batch-retry-dialog-cancel"
            className="cursor-pointer"
            disabled={isPending}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid="batch-retry-dialog-start"
            className="cursor-pointer"
            isLoading={isPending}
            disabled={isPending || (!fromRunId && (naLoading || !selected || selected.count === 0))}
            onClick={handleStart}
          >
            {!fromRunId && naLoading ? 'Loading…' : 'Start retry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </>
  );
}
